/**
 * Cookie storage utility for Zustand persistence
 *
 * Provides secure cookie storage with:
 * - secure: true (only sent over HTTPS in production)
 * - sameSite: 'lax' (CSRF protection while allowing top-level navigation)
 *
 * Note: Cookie max-age should be >= Okta refresh token TTL (configured in tenant policy).
 * Since Okta doesn't expose refresh token expiration in the token response,
 * we use a static value that should match or exceed the tenant's configuration.
 */

/**
 * Cookie max-age in days
 * Should match or exceed Okta refresh token TTL configured in tenant password policy
 */
const COOKIE_MAX_AGE_DAYS = 90;

/**
 * Get cookie options for secure storage
 */
function getCookieOptions(): string {
  const isSecure = window.location.protocol === "https:";
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60; // Convert to seconds

  const options = [`max-age=${maxAge}`, "path=/", "samesite=lax"];

  if (isSecure) {
    options.push("secure");
  }

  return options.join("; ");
}

/**
 * Set a cookie value
 */
function setCookie(name: string, value: string): void {
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${name}=${encodedValue}; ${getCookieOptions()}`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  const cookies = document.cookie.split("; ");
  const cookie = cookies.find((c) => c.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  const value = cookie.substring(name.length + 1);
  return decodeURIComponent(value);
}

/**
 * Remove a cookie by setting its expiration to the past
 */
function removeCookie(name: string): void {
  document.cookie = `${name}=; max-age=0; path=/`;
}

/**
 * Cookie storage adapter for Zustand's persist middleware
 *
 * Usage:
 * ```
 * persist(
 *   (set, get) => ({ ... }),
 *   {
 *     name: 'my-store',
 *     storage: createJSONStorage(() => cookieStorage),
 *   }
 * )
 * ```
 */
export const cookieStorage = {
  setItem: (key: string, value: string): void => {
    setCookie(key, value);
  },
  getItem: (key: string): string | null => {
    return getCookie(key);
  },
  removeItem: (key: string): void => {
    removeCookie(key);
  },
};
